'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { EDIT_FORBIDDEN, guardIllustrationEdit } from '@/contexts/pipeline-v2/edits/guardAction.server';

export async function generatePage(input: {
  bookId: string;
  pageNumber: number;
  forceRegenerate?: boolean;
  /** Image render quality. Left out = whatever the book pipeline renders pages at
   *  (admin config, then the portal's own default) — a page redrawn by hand used to
   *  be pinned to 'low' here and so came back WORSE than the page it replaced. */
  quality?: 'draft' | 'low' | 'medium' | 'final';
  /** Why a finished page is drawn again: 'ours' (it does not look right),
   *  'taste' (I want it different) or 'failed' (it broke). Five redraws per
   *  story are ours (`SHARED.MONEY.R13`), and the reason is what makes the
   *  quota measurable at all (gap 10). */
  redrawCause?: 'ours' | 'taste' | 'failed';
}): Promise<ActionResult<{ jobId: string | null; imageUrl: string | null }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // «Перерисовать страницу» is an edit after ready: it waits for the generation / an open print order
    if (input.forceRegenerate) {
      const blocked = await guardIllustrationEdit({ illustrationId: input.bookId, familyId, kind: 'redraw_page' });
      if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };
    }

    const { IllustrationGenerationService } = await import('@/contexts/illustrations/services/BookGenerationService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { getImageGenerationQueue } = await import('@/contexts/ai/infrastructure/imageGenerationQueue');

    const service = new IllustrationGenerationService(
      getIllustrationRepository(),
      getCharacterRepository(),
      getTokenManager(),
      getImageGenerationQueue(),
    );

    const result = await service.generatePage({
      bookId: input.bookId,
      familyId,
      pageNumber: input.pageNumber,
      forceRegenerate: input.forceRegenerate ?? false,
      quality: input.quality,
      ...(input.redrawCause ? { redrawCause: input.redrawCause } : {}),
    });

    if (!result.success) {
      return { success: false, error: 'Page generation failed' };
    }
    return {
      success: true,
      data: { jobId: result.jobId || null, imageUrl: result.imageUrl || null },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generatePage', e) };
  }
}
